// THE SETTINGS ROUTE (bead hf-e3b-circle-people-r3z.2, composing bead hf-7o6).
//
// `/c/<ref>/settings` now has two owners' worth of content, and they belong together
// rather than on separate routes:
//
//   - PLAN SETTINGS, which an organiser changes for everybody (this bead).
//   - YOUR CALENDAR LINK, which each member manages for themselves (hf-7o6).
//
// Different capabilities, different audiences, one place a person looks — which is
// what "settings" means to somebody who is not thinking about capabilities. The plan
// settings come first because they are the shared thing; the personal link follows.
//
// It lives in `apps/web/src/convex/` beside `ConvexSetupProvider.tsx` for the same
// reason `CalendarFeedRoute.tsx` does: the feature modules stay pure component trees
// over view models, and the subscriptions live here. It also keeps `App.tsx` — a
// heavily shared file — to a single line for this whole surface.

import { useMutation, useQuery } from "convex/react";
import { useCallback, useState } from "react";

import {
  AdmissionAllowlist,
  CircleSettings,
  useCircleSettings,
  type AdmissionAllowlistOutcome,
  type BlockDefinitionsDraft,
} from "../features/circle";
import { NotificationPreferencesRoute } from "../features/notifications";
import { useConnectivity } from "../offline/offline";
import { CalendarFeedRoute } from "./CalendarFeedRoute";
import { InvitationAdminRoute } from "./InvitationRoute";
import { IdentityReconciliationRoute } from "./IdentityReconciliationRoute";
import {
  addAdmissionEmailMutation,
  admissionAllowlistQuery,
  removeAdmissionEntryMutation,
} from "./admission";
import {
  circleSettingsQuery,
  setBlockDefinitionsMutation,
  setDirectRequestExpiryPolicyMutation,
  setDisplayNameMutation,
  setSupportedPersonNameMutation,
  setSupportedPersonPrintContactNameMutation,
  setSupportedPersonPrintContactMutation,
  setTimeZoneMutation,
} from "./circleSettings";

export interface SettingsRouteProps {
  readonly circleId: string;
}

export function SettingsRoute({ circleId }: SettingsRouteProps) {
  return <CircleSettingsPanel circleId={circleId} />;
}

function CircleSettingsPanel({ circleId }: { readonly circleId: string }) {
  const settings = useQuery(circleSettingsQuery, { circleId });
  const { actionsPaused } = useConnectivity();

  const setBlockDefinitions = useMutation(setBlockDefinitionsMutation);
  const setDirectRequestExpiryPolicy = useMutation(setDirectRequestExpiryPolicyMutation);
  const setTimeZone = useMutation(setTimeZoneMutation);
  const setDisplayName = useMutation(setDisplayNameMutation);
  const setSupportedPersonName = useMutation(setSupportedPersonNameMutation);
  const setPrintContact = useMutation(setSupportedPersonPrintContactMutation);
  const setPrintContactName = useMutation(setSupportedPersonPrintContactNameMutation);

  const controller = useCircleSettings({
    setBlockDefinitions: useCallback(
      (blockDefinitions: BlockDefinitionsDraft) => {
        if (actionsPaused)
          return Promise.reject(new Error("Connection is not ready for a setting change."));
        return setBlockDefinitions({ circleId, blockDefinitions });
      },
      [actionsPaused, circleId, setBlockDefinitions],
    ),
    setDirectRequestExpiryPolicy: useCallback(
      (unansweredTtlDays: number) => {
        if (actionsPaused)
          return Promise.reject(new Error("Connection is not ready for a setting change."));
        return setDirectRequestExpiryPolicy({ circleId, unansweredTtlDays });
      },
      [actionsPaused, circleId, setDirectRequestExpiryPolicy],
    ),
    setTimeZone: useCallback(
      (timeZone: string) => {
        if (actionsPaused)
          return Promise.reject(new Error("Connection is not ready for a setting change."));
        return setTimeZone({ circleId, timeZone });
      },
      [actionsPaused, circleId, setTimeZone],
    ),
    setDisplayName: useCallback(
      (displayName: string) => {
        if (actionsPaused)
          return Promise.reject(new Error("Connection is not ready for a setting change."));
        return setDisplayName({ circleId, displayName });
      },
      [actionsPaused, circleId, setDisplayName],
    ),
    setSupportedPersonName: useCallback(
      (chosenName: string) => {
        if (actionsPaused)
          return Promise.reject(new Error("Connection is not ready for a setting change."));
        return setSupportedPersonName({ circleId, chosenName });
      },
      [actionsPaused, circleId, setSupportedPersonName],
    ),
    setPrintContact: useCallback(
      (printContactText: string) => {
        if (actionsPaused)
          return Promise.reject(new Error("Connection is not ready for a setting change."));
        return setPrintContact({ circleId, printContactText });
      },
      [actionsPaused, circleId, setPrintContact],
    ),
    setPrintContactName: useCallback(
      (printContactPersonName: string) => {
        if (actionsPaused)
          return Promise.reject(new Error("Connection is not ready for a setting change."));
        return setPrintContactName({ circleId, printContactPersonName });
      },
      [actionsPaused, circleId, setPrintContactName],
    ),
  });

  // `undefined` is Convex still loading; `null` is a real answer meaning the circle is
  // gone, and the shell's route boundary already has a surface for that.
  if (settings === undefined || settings === null) return null;

  return (
    <div className="settings-route">
      <CircleSettings actionsDisabled={actionsPaused} controller={controller} settings={settings} />
      {/* The query itself needs plan.manage, so the server-derived affordance controls
          whether this subtree is mounted. A reader never probes the admin function. */}
      {settings.canManageSettings ? (
        <>
          <AdmissionAllowlistPanel actionsPaused={actionsPaused} circleId={circleId} />
          <InvitationAdminRoute circleId={circleId} />
          <IdentityReconciliationRoute circleId={circleId} />
        </>
      ) : null}
      {/* ONLY when the server says this member may have one. The feed's own query is
          `plan.export`-gated, and rendering it for somebody without that capability
          throws at the boundary — which the shell's route boundary turns into "this
          page isn't available", taking the plan settings down with it. */}
      {settings.canUseCalendarFeed ? (
        <CalendarFeedRoute circleId={circleId} headingLevel={2} />
      ) : null}
      {/* HOW WE REACH YOU (NTF-017/018; bead hf-e7-notifications-742.3). Personal, like
          the calendar link, and gated at `plan.view` on the server — so unlike the feed
          it is offered to everybody who can read the plan at all. Deciding how you are
          contacted is not a privilege. */}
      <NotificationPreferencesRoute circleId={circleId} />
    </div>
  );
}

function AdmissionAllowlistPanel({
  circleId,
  actionsPaused,
}: {
  readonly circleId: string;
  readonly actionsPaused: boolean;
}) {
  const allowlist = useQuery(admissionAllowlistQuery, { circleId });
  const addEmail = useMutation(addAdmissionEmailMutation);
  const removeEntry = useMutation(removeAdmissionEntryMutation);
  const [outcome, setOutcome] = useState<AdmissionAllowlistOutcome>({ status: "idle" });

  const onAdd = useCallback(
    async (email: string) => {
      if (actionsPaused) {
        setOutcome({ status: "unavailable" });
        return;
      }
      setOutcome({ status: "working" });
      try {
        const result = await addEmail({ circleId, email });
        if (result.status !== "applied") {
          setOutcome({ status: "unavailable" });
        } else if (result.value.ok) {
          setOutcome({ status: "added" });
        } else {
          setOutcome(
            result.value.code === "invalid_email"
              ? { status: "invalid" }
              : { status: "unavailable" },
          );
        }
      } catch {
        setOutcome({ status: "unavailable" });
      }
    },
    [actionsPaused, addEmail, circleId],
  );

  const onRemove = useCallback(
    async (
      allowlistEntryId: NonNullable<typeof allowlist>["entries"][number]["allowlistEntryId"],
    ) => {
      if (actionsPaused) {
        setOutcome({ status: "unavailable" });
        return;
      }
      setOutcome({ status: "working" });
      try {
        const result = await removeEntry({ circleId, allowlistEntryId });
        setOutcome(
          result.status === "applied" && result.value.ok
            ? { status: "removed" }
            : { status: "unavailable" },
        );
      } catch {
        setOutcome({ status: "unavailable" });
      }
    },
    [actionsPaused, circleId, removeEntry],
  );

  if (allowlist === undefined) return null;
  return (
    <AdmissionAllowlist
      actionsDisabled={actionsPaused}
      entries={allowlist.entries}
      headingLevel={2}
      onAdd={onAdd}
      onRemove={onRemove}
      outcome={outcome}
    />
  );
}
